import type { ApiClient } from '../api/client';
import type { CompleteUploadResponse } from '../api/types';

/** Spec §6: 4–8 MB blocks. Used when the server does not dictate a block size. */
export const DEFAULT_BLOCK_SIZE = 4 * 1024 * 1024;

export interface UploadParams {
  folderId: string;
  documentTypeCode: string;
  file: File;
  title?: string;
}

/**
 * Create → stage blocks (0-based block numbers) → complete. Progress is 0..1; the last 5% is the
 * server-side complete step (MIME sniff + hash) so the bar does not sit at 100% while it runs.
 */
export async function uploadFile(
  api: ApiClient,
  params: UploadParams,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<CompleteUploadResponse> {
  const { file } = params;
  const created = await api.uploads.create(
    {
      folderId: params.folderId,
      documentTypeCode: params.documentTypeCode,
      filename: file.name,
      sizeBytes: file.size,
      contentType: file.type || undefined,
      title: params.title,
    },
    signal,
  );
  const blockSize = created.blockSize > 0 ? created.blockSize : DEFAULT_BLOCK_SIZE;
  const blockCount = Math.max(1, Math.ceil(file.size / blockSize));
  for (let n = 0; n < blockCount; n += 1) {
    if (signal?.aborted) throw new DOMException('Upload cancelled', 'AbortError');
    const chunk = file.slice(n * blockSize, Math.min(file.size, (n + 1) * blockSize));
    await api.uploads.putBlock(created.uploadId, n, chunk, signal);
    onProgress(((n + 1) / blockCount) * 0.95);
  }
  const done = await api.uploads.complete(created.uploadId, signal);
  onProgress(1);
  return done;
}
